import { CustomLoader } from "@admin/components/ui/custom-loader";
import { Card, CardContent } from "../../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../../components/data-table";
import { Button } from "../../../components/ui/button";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  pressCoverageAPI,
  type PressCoverage,
  type PressCoverageCreatePayload,
  type PaginationParams,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

const MEDIA_TYPE_OPTIONS = ["News", "Video", "Magazine", "Online", "Print"];

const initialFormState: PressCoverageCreatePayload = {
  title: "",
  date: "",
  media_type: MEDIA_TYPE_OPTIONS[0],
  link: "",
  is_enabled: true,
};

function parseListResponse<T>(res: unknown): {
  list: T[];
  count: number;
} {
  if (!res || typeof res !== "object") {
    return { list: [], count: 0 };
  }
  const data = res as Record<string, unknown>;
  const list = (data.results ?? data.data ?? []) as T[];
  const count =
    (data.count as number) ??
    (data.total_count as number) ??
    (Array.isArray(list) ? list.length : 0);
  return { list: Array.isArray(list) ? list : [], count };
}

export default function PressCoveragePage() {
  const [pressItems, setPressItems] = useState<PressCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [form, setForm] =
    useState<PressCoverageCreatePayload>(initialFormState);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadPressCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await pressCoverageAPI.list(params);
      const { list, count } = parseListResponse<PressCoverage>(res);
      setPressItems(list);
      setTotalItems(count);
      setTotalPages(Math.ceil(count / pageSize) || 0);
    } catch {
      toast.error("Failed to load press coverage");
      setPressItems([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadPressCoverage();
  }, [loadPressCoverage]);

  const resetForm = () => {
    setForm(initialFormState);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditModal = (item: PressCoverage) => {
    setForm({
      title: item.title ?? "",
      date: item.date ?? "",
      media_type: item.media_type ?? MEDIA_TYPE_OPTIONS[0],
      link: item.link ?? "",
      is_enabled: item.is_enabled ?? true,
    });
    setEditingId(item.id);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Please enter a title.");
      return;
    }
    if (!form.date) {
      toast.error("Please select a date.");
      return;
    }
    if (!form.link.trim()) {
      toast.error("Please enter a link.");
      return;
    }
    setSaving(true);
    try {
      if (editingId !== null && editingId !== undefined) {
        await pressCoverageAPI.update(editingId, form);
        toast.success("Press coverage updated successfully!");
      } else {
        await pressCoverageAPI.create(form);
        toast.success("Press coverage created successfully!");
      }
      await loadPressCoverage();
      setIsDialogOpen(false);
      resetForm();
    } catch {
      toast.error(
        editingId !== null && editingId !== undefined
          ? "Failed to update press coverage"
          : "Failed to create press coverage"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    setIsDialogOpen(open);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await pressCoverageAPI.delete(deleteId);
      toast.success("Press coverage deleted successfully!");
      await loadPressCoverage();
      setDeleteId(null);
    } catch {
      toast.error("Failed to delete press coverage");
    }
  };

  const isEditMode = editingId !== null && editingId !== undefined;

  const tableColumns = useMemo<TableColumnDef<PressCoverage>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "title",
        label: "Title",
        width: 220,
        renderCell: press => <span className="font-medium">{press.title}</span>,
      },
      {
        id: "date",
        label: "Date",
        width: 120,
        renderCell: press => press.date ?? "—",
      },
      {
        id: "type",
        label: "Media Type",
        width: 120,
        renderCell: press => press.media_type,
      },
      {
        id: "link",
        label: "Link",
        width: 200,
        renderCell: press =>
          press.link ? (
            <a
              href={press.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              {press.link}
            </a>
          ) : (
            "—"
          ),
      },
      {
        id: "enabled",
        label: "Enabled",
        width: 80,
        renderCell: press => (press.is_enabled ? "Yes" : "No"),
      },
      {
        id: "actions",
        label: "Action",
        width: 100,
        alwaysVisible: true,
        renderCell: item => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openEditModal(item)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeleteId(item.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Press Coverage</h1>
          <p className="text-muted-foreground">
            Add and manage press and media coverage entries
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAddModal}>
              <Plus className="h-4 w-4" />
              Add Press
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {isEditMode ? "Edit Press Coverage" : "Add Press Coverage"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Federation Expands Nationwide"
                  value={form.title}
                  onChange={e =>
                    setForm(prev => ({ ...prev, title: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={e =>
                    setForm(prev => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Media Type</Label>
                <Select
                  value={form.media_type}
                  onValueChange={value =>
                    setForm(prev => ({ ...prev, media_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select media type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="link">Link</Label>
                <Input
                  id="link"
                  placeholder="https://example.com/news/article"
                  value={form.link}
                  onChange={e =>
                    setForm(prev => ({ ...prev, link: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label htmlFor="is_enabled" className="cursor-pointer">
                  Enable entry (visible to users)
                </Label>
                <Switch
                  id="is_enabled"
                  checked={form.is_enabled}
                  onCheckedChange={checked =>
                    setForm(prev => ({ ...prev, is_enabled: checked }))
                  }
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <CustomLoader size="xs" className="mr-2" />}
                  {isEditMode ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Press Coverage (${totalItems})`}
              tableId="super-admin-cms-press-coverage"
              columns={tableColumns}
              data={pressItems}
              getRowKey={press => String(press.id)}
              emptyMessage='No press coverage yet. Click "Add Press" to create one.'
            />
          )}

          {!loading && totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null && deleteId !== undefined}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete press coverage</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              press coverage entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
